import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveImg } from "@/lib/apiClient";
import Seo from "@/components/Seo";
import PublicLayout from "@/components/PublicLayout";
import { Breadcrumbs } from "@/components/Marketing";
import { AvailableBadge, PhvBadge } from "@/components/Badges";
import WhatsAppButton from "@/components/WhatsAppButton";
import CallButton from "@/components/CallButton";
import { waMessages, fmtPrice, vehicleName } from "@/lib/whatsapp";
import { Loader2, Gauge, Calendar, Fuel, Cog, DoorOpen, Palette, BadgeCheck, Hash } from "lucide-react";

export default function VehicleDetail() {
  const { slug } = useParams();
  const [v, setV] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setV(null); setNotFound(false); setActiveImg(0);
    api.get(`/vehicles/${slug}`).then((r) => setV(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <PublicLayout>
        <Seo title="Vehicle not found" noindex path={`/cars/${slug}`} />
        <div className="container-x py-24 text-center">
          <h1 className="font-heading text-3xl font-bold">Vehicle not found</h1>
          <p className="mt-3 text-zinc-500">This vehicle may have been removed.</p>
          <Link to="/cars" className="mt-6 inline-block font-bold text-[#00E676] underline">Back to all cars</Link>
        </div>
      </PublicLayout>
    );

  if (!v)
    return (
      <PublicLayout>
        <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-zinc-400" /></div>
      </PublicLayout>
    );

  const name = vehicleName(v);
  const images = [v.main_image, ...(v.gallery_images || [])].filter(Boolean);
  const priceNo = fmtPrice(v.weekly_price_without_insurance);
  const priceIns = fmtPrice(v.weekly_price_with_insurance);
  const rented = v.availability === "rented";
  const coming = v.availability === "coming_soon";

  const specs = [
    { icon: Calendar, label: "Year", value: v.year },
    { icon: Gauge, label: "Mileage", value: `${v.mileage?.toLocaleString()} miles` },
    { icon: Fuel, label: "Fuel", value: v.fuel_type },
    { icon: Cog, label: "Transmission", value: v.transmission },
    { icon: DoorOpen, label: "Doors", value: v.doors },
    { icon: Palette, label: "Colour", value: v.colour || "—" },
    { icon: BadgeCheck, label: "PHV Licence", value: v.phv_licence_status },
    ...(v.show_registration && v.registration ? [{ icon: Hash, label: "Registration", value: v.registration }] : []),
  ];

  const waMsg = rented ? waMessages.rented(v) : waMessages.vehicle(v);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Vehicle",
    name,
    vehicleModelDate: String(v.year),
    fuelType: v.fuel_type,
    vehicleTransmission: v.transmission,
    mileageFromOdometer: { "@type": "QuantitativeValue", value: v.mileage, unitCode: "SMI" },
    brand: { "@type": "Brand", name: v.make },
    ...(v.weekly_price_without_insurance
      ? { offers: { "@type": "Offer", price: v.weekly_price_without_insurance, priceCurrency: "GBP",
            availability: v.availability === "available" ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" } }
      : {}),
  };

  return (
    <PublicLayout>
      <Seo
        title={v.seo_title || `${name} – Manchester PHV Rental`}
        description={v.seo_description}
        path={`/cars/${v.slug}`}
        ogImage={images[0] ? resolveImg(images[0]) : undefined}
        jsonLd={jsonLd}
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Cars", to: "/cars" }, { label: name }]} />

      <div className="container-x grid gap-10 pb-16 lg:grid-cols-5">
        {/* Gallery */}
        <div className="lg:col-span-3">
          <div className="relative overflow-hidden border border-border">
            <div className="absolute left-3 top-3 z-10 flex flex-wrap gap-2">
              <AvailableBadge status={v.availability} />
            </div>
            {images[activeImg] ? (
              <img src={resolveImg(images[activeImg])} alt={`${name} — image ${activeImg + 1}`} className="aspect-[4/3] w-full object-cover" data-testid="detail-main-image" />
            ) : (
              <div className="flex aspect-[4/3] items-center justify-center bg-surface text-zinc-400">No image</div>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-3 overflow-x-auto">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`h-20 w-28 shrink-0 overflow-hidden border-2 ${i === activeImg ? "border-[#00E676]" : "border-transparent"}`}>
                  <img src={resolveImg(img)} alt={`${name} thumbnail ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:col-span-2">
          <div className="mb-3"><PhvBadge status={v.phv_licence_status} /></div>
          <h1 className="font-heading text-3xl font-black uppercase leading-tight tracking-tighter sm:text-4xl">
            {name} – Manchester PHV Rental
          </h1>

          <div className="mt-6 border border-border p-6">
            {priceNo && (
              <div data-testid="detail-price-no-insurance">
                <div className="font-heading text-5xl font-black leading-none text-ink">{priceNo}<span className="text-xl text-zinc-400">/week</span></div>
                <div className="mt-1 text-xs font-bold uppercase tracking-wider text-zinc-500">Vehicle Rental Without Insurance</div>
              </div>
            )}
            {v.insurance_available && priceIns && (
              <div className="mt-5 border-t border-border pt-5" data-testid="detail-price-with-insurance">
                <div className="font-heading text-4xl font-black leading-none text-zinc-800">{priceIns}<span className="text-lg text-zinc-400">/week</span></div>
                <div className="mt-1 text-xs font-bold uppercase tracking-wider text-zinc-500">Rental With Insurance</div>
              </div>
            )}
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-1 text-sm text-zinc-600">
              {v.deposit != null && <span><strong>Deposit:</strong> £{Math.round(v.deposit)}</span>}
              {v.minimum_rental_weeks != null && <span><strong>Minimum Rental:</strong> {v.minimum_rental_weeks} Week{v.minimum_rental_weeks === 1 ? "" : "s"}</span>}
            </div>

            {rented && (
              <div className="mt-5 rounded-lg bg-surface p-4 text-sm text-zinc-600">
                <p><strong>Currently Unavailable.</strong> This vehicle is out on hire right now. Looking for something similar? We'll tell you what's available.</p>
                <Link to="/cars" className="mt-3 inline-flex items-center gap-1.5 font-bold text-zinc-900 underline">View Available Cars</Link>
              </div>
            )}
            {coming && (
              <p className="mt-5 rounded-lg bg-amber-50 p-4 text-sm text-amber-800">
                <strong>Coming Soon</strong> – WhatsApp us to register your interest.
              </p>
            )}

            <div className="mt-6">
              <WhatsAppButton size="lg" className="w-full" message={waMsg} context={`vehicle:${v.slug}`} testid="detail-whatsapp">
                {rented ? "WhatsApp Us About Similar Cars" : "WhatsApp to Rent This Car"}
              </WhatsAppButton>
              <CallButton size="lg" className="mt-3 w-full" variant="dark" context={`vehicle:${v.slug}`} testid="detail-call" showNumber>
                Call Us
              </CallButton>
            </div>
          </div>

          {v.features?.length > 0 && (
            <div className="mt-6">
              <h3 className="font-heading text-lg font-bold">Features</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {v.features.map((f) => (
                  <span key={f} className="rounded-full border border-border px-3 py-1 text-sm text-zinc-700">{f}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Specs + description */}
      <div className="container-x grid gap-10 pb-20 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <h2 className="font-heading text-2xl font-bold">Vehicle Details</h2>
          <dl className="mt-5 grid grid-cols-2 gap-px overflow-hidden border border-border bg-border sm:grid-cols-3">
            {specs.map((s) => (
              <div key={s.label} className="bg-white p-4">
                <s.icon className="h-5 w-5 text-[#00E676]" />
                <dt className="mt-2 text-[11px] font-bold uppercase tracking-wider text-zinc-500">{s.label}</dt>
                <dd className="mt-0.5 font-semibold text-zinc-900">{s.value}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="lg:col-span-2">
          <h2 className="font-heading text-2xl font-bold">About This Vehicle</h2>
          <p className="mt-4 leading-relaxed text-zinc-600">{v.full_description || v.short_description}</p>
        </div>
      </div>

      {/* Internal links */}
      <div className="container-x pb-20">
        <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Explore More</h2>
        <ul className="mt-4 flex flex-wrap gap-3">
          {[
            { to: "/cars", label: "All available cars" },
            { to: "/taxi-rental-manchester", label: "Taxi Rental Manchester" },
            { to: "/private-hire-car-rental-manchester", label: "Private hire car rental" },
            { to: v.insurance_available ? "/taxi-rental-with-insurance-manchester" : "/taxi-rental-without-insurance-manchester", label: v.insurance_available ? "Rental with insurance" : "Rental without insurance" },
            { to: "/guides", label: "Driver guides" },
          ].map((l, i) => (
            <li key={i}>
              <Link to={l.to} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-surface">{l.label}</Link>
            </li>
          ))}
        </ul>
      </div>
    </PublicLayout>
  );
}
